// 移植自 component/textarea-caret-position（MIT）的 mirror-div 技术：
// 构造与 textarea 同字体/同盒模型的隐藏 div，复制 caret 前文本，用一个内联 span
// 标记 caret 位置，读取 span 的 offsetTop/offsetLeft 得到像素坐标。
// jsdom 无真实布局，offset 量到的是 0/兜底，但函数本身不抛错（见同名测试）。

export interface CaretCoordinates {
  top: number;
  left: number;
  height: number;
}

// 需要从 textarea 复制到 mirror div 的样式属性，保证换行/折行一致。
const COPIED_PROPERTIES = [
  'direction',
  'boxSizing',
  'width',
  'height',
  'overflowX',
  'overflowY',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'borderStyle',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'fontStyle',
  'fontVariant',
  'fontWeight',
  'fontStretch',
  'fontSize',
  'lineHeight',
  'fontFamily',
  'textAlign',
  'textTransform',
  'textIndent',
  'letterSpacing',
  'wordSpacing',
  'tabSize',
] as const;

function pxOr(value: string, fallback: number): number {
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? fallback : n;
}

export function getCaretCoordinates(element: HTMLTextAreaElement, position: number): CaretCoordinates {
  const computed = window.getComputedStyle(element);
  const fontSize = pxOr(computed.fontSize, 16);
  // lineHeight 可能是 "normal" → 兜底 fontSize*1.2，保证 height>0。
  const lineHeight = pxOr(computed.lineHeight, Math.round(fontSize * 1.2));

  const div = document.createElement('div');
  const style = div.style;
  style.position = 'absolute';
  style.visibility = 'hidden';
  style.whiteSpace = 'pre-wrap';
  style.wordWrap = 'break-word';
  // 用 camelCase 索引访问拷样式（CSSOM 支持 style[prop]=computed[prop]）。
  // 不能用 setProperty/getPropertyValue——它们要 kebab-case，传 camelCase 会复制为空。
  const styleMap = style as unknown as Record<string, string>;
  const computedMap = computed as unknown as Record<string, string>;
  COPIED_PROPERTIES.forEach((prop) => {
    styleMap[prop] = computedMap[prop];
  });
  document.body.appendChild(div);

  try {
    div.textContent = element.value.substring(0, position);
    const span = document.createElement('span');
    // span 内容非空才能拿到稳定的 offset；用剩余文本或一个占位点。
    span.textContent = element.value.substring(position) || '.';
    div.appendChild(span);

    return {
      top: span.offsetTop + pxOr(computed.borderTopWidth, 0),
      left: span.offsetLeft + pxOr(computed.borderLeftWidth, 0),
      height: lineHeight,
    };
  } finally {
    document.body.removeChild(div); // 无论是否抛错都清理
  }
}
